import { useCallback, useEffect, useState } from "react";
import { Avatar, Button, SkeletonText } from "@astrum/ui";
import { api, ApiError } from "../../lib/api.js";
import type { Message } from "../../lib/api.js";
import { useRealtimeFrames } from "../../lib/realtime-context.js";
import { MessageHit } from "./ChannelFilters.js";

type InboxThread = {
  root: Message;
  channelId: string;
  unreadCount: number;
  lastReply: { authorName: string; body: string; createdAt: string } | null;
};

type InboxPage = { threads: InboxThread[]; hasMore: boolean; nextCursor: string | null; unreadThreads: number };

const INBOX_PAGE = 25;

/**
 * The Threads view: threads the viewer follows, most recently active first,
 * with how many replies they have not seen. Unread state is the server's, so
 * it agrees across devices. Refetched when a reply lands in a followed thread.
 */
export function ThreadsInbox({ onOpenThread }: { onOpenThread: (channelId: string, rootId: string) => void }) {
  const [threads, setThreads] = useState<InboxThread[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(() => {
    api
      .get<InboxPage>(`/me/threads?limit=${INBOX_PAGE}`)
      .then((res) => {
        setThreads(res.threads);
        setCursor(res.nextCursor);
        setError(null);
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : "Could not load your threads."));
  }, []);

  useEffect(load, [load]);

  useRealtimeFrames((frame) => {
    if (frame.t === "thread_unread" || frame.t === "thread") load();
  });

  const loadMore = () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    api
      .get<InboxPage>(`/me/threads?limit=${INBOX_PAGE}&cursor=${encodeURIComponent(cursor)}`)
      .then((res) => {
        setThreads((prev) => [...(prev ?? []), ...res.threads.filter((t) => !prev?.some((p) => p.root.id === t.root.id))]);
        setCursor(res.nextCursor);
      })
      .catch(() => undefined)
      .finally(() => setLoadingMore(false));
  };

  if (error) return <p className="chat-list__no-match">{error}</p>;
  if (!threads)
    return (
      <div className="chat-list__loading">
        <SkeletonText lines={3} />
      </div>
    );
  if (threads.length === 0)
    return <p className="chat-list__no-match">You are not following any threads yet. Reply in one, or get mentioned in one.</p>;

  return (
    <div className="chat-list__hits">
      {threads.map((thread) => {
        const replies = thread.root.thread?.replyCount ?? 0;
        return (
          <button
            key={thread.root.id}
            type="button"
            className={`chat-list__hit chat-thread-hit ${thread.unreadCount ? "is-unread" : ""}`}
            onClick={() => onOpenThread(thread.channelId, thread.root.id)}
          >
            <Avatar name={thread.root.authorName} size={22} />
            <span className="chat-list__hit-body">
              <span className="chat-list__hit-meta">
                <strong>{thread.root.authorName}</strong>
                <span>
                  {replies} {replies === 1 ? "reply" : "replies"}
                </span>
              </span>
              <span className="chat-list__hit-text">{thread.root.body || "Message deleted"}</span>
              {thread.lastReply && (
                <span className="chat-thread-hit__last">
                  {thread.lastReply.authorName}: {thread.lastReply.body}
                </span>
              )}
            </span>
            {thread.unreadCount > 0 && (
              <span className="chat-list__unread" aria-label={`${thread.unreadCount} unread`}>
                {thread.unreadCount > 99 ? "99+" : thread.unreadCount}
              </span>
            )}
          </button>
        );
      })}
      {cursor && (
        <Button size="sm" variant="ghost" loading={loadingMore} onClick={loadMore}>
          Show more
        </Button>
      )}
    </div>
  );
}

/** The personal "saved for later" list, newest saved first, paginated. */
export function SavedList({ onOpenChannel }: { onOpenChannel: (channelId: string, messageId?: string) => void }) {
  const [messages, setMessages] = useState<Message[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    api
      .get<{ messages: Message[]; hasMore: boolean }>(`/me/saved-messages?limit=${INBOX_PAGE}`)
      .then((res) => {
        setMessages(res.messages);
        setHasMore(res.hasMore);
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : "Could not load your saved items."));
  }, []);

  const loadMore = () => {
    const last = messages?.[messages.length - 1];
    if (!last || loadingMore) return;
    setLoadingMore(true);
    api
      .get<{ messages: Message[]; hasMore: boolean }>(`/me/saved-messages?limit=${INBOX_PAGE}&before=${last.id}`)
      .then((res) => {
        setMessages((prev) => [...(prev ?? []), ...res.messages]);
        setHasMore(res.hasMore);
      })
      .catch(() => undefined)
      .finally(() => setLoadingMore(false));
  };

  if (error) return <p className="chat-list__no-match">{error}</p>;
  if (!messages)
    return (
      <div className="chat-list__loading">
        <SkeletonText lines={3} />
      </div>
    );
  if (messages.length === 0)
    return <p className="chat-list__no-match">Nothing saved yet. Use "Save for later" on a message.</p>;

  return (
    <div className="chat-list__hits">
      {messages.map((message) => (
        <MessageHit key={message.id} message={message} onOpen={() => onOpenChannel(message.channelId, message.id)} />
      ))}
      {hasMore && (
        <Button size="sm" variant="ghost" loading={loadingMore} onClick={loadMore}>
          Show more
        </Button>
      )}
    </div>
  );
}
